/**
 * QualityDropdownRow — Sprint A1
 *
 * Reusable 44px row in both CompliantOrb and DueOrb dropdowns.
 *
 * Layout:
 *   ✓  Depression Screening        captured today
 *      PHQ-9 = 4                   Good through Apr 2027
 *
 * - Amber tint when is_critically_soon
 * - "(N days)" in expiration label when ≤90 days
 * - Action button when showActions = true (live encounter, due measures only)
 * - Minimum 44px height (touch target compliance)
 */
import React from 'react';
import { Check, Clock, AlertCircle, Zap } from 'lucide-react';
import { Button } from '../ui/button';

function formatEvidence(chip) {
  const ev = chip.evidence_this_visit?.[0];
  if (!ev) return null;
  const val = ev.evidence_value;
  if (val?.systolic && val?.diastolic) return `BP ${val.systolic}/${val.diastolic}`;
  if (val?.score !== undefined) return `Score ${val.score}`;
  if (val?.bmi) return `BMI ${val.bmi}`;
  if (ev.label) return ev.label;
  return ev.source?.replace(/_/g, ' ');
}

export function QualityDropdownRow({ chip, showActions = false, onAction }) {
  const expiry = chip.expiration || {};
  const isCritical = expiry.is_critically_soon;
  const isExpiring = expiry.is_expiring_soon;
  const isLifetime = expiry.is_lifetime;
  const capturedToday = chip.captured_this_encounter;
  const evidenceSummary = formatEvidence(chip);
  const status = chip.status;

  const rowBg = isCritical
    ? 'bg-amber-50/60 hover:bg-amber-50'
    : 'hover:bg-accent/50';

  const StatusIcon = status === 'compliant'
    ? <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
    : status === 'overdue'
      ? <AlertCircle className="w-3.5 h-3.5 text-red-500 shrink-0 mt-0.5" />
      : <Clock className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />;

  const defaultOrders = {
    'qm_crc_screening': 'order_cologuard',
    'qm_diabetes_a1c': 'order_a1c',
    'qm_nephropathy_monitoring': 'order_microalbumin',
    'qm_flu_vaccine': 'administer_flu',
    'qm_breast_screening': 'order_mammogram',
  };

  return (
    <div
      className={`flex items-start gap-2.5 px-3 py-2.5 border-b last:border-0 transition-colors ${rowBg}`}
      style={{ minHeight: '44px' }}
      data-testid={`quality-row-${chip.measure_id}`}>
      {StatusIcon}
      <div className="flex-1 min-w-0">
        {/* Row 1: Label + captured-today tag */}
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-semibold text-foreground truncate">{chip.short_label}</span>
          {capturedToday && (
            <span className="text-[8px] font-semibold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full border border-emerald-200 shrink-0">
              captured today
            </span>
          )}
        </div>
        {/* Row 2: Evidence summary */}
        {evidenceSummary && (
          <p className="text-[10px] text-muted-foreground mt-0.5">{evidenceSummary}</p>
        )}
        {/* Row 3: Expiration label */}
        {(expiry.expiration_label || isLifetime) && (
          <p className={`text-[10px] mt-0.5 ${isCritical ? 'text-amber-700 font-medium' : 'text-muted-foreground'}`}>
            {isLifetime ? 'Lifetime' : expiry.expiration_label}
          </p>
        )}
        {/* Action button — only for due measures in live encounter */}
        {showActions && status !== 'compliant' && onAction && (
          <div className="mt-1">
            {defaultOrders[chip.measure_id] ? (
              <Button
                size="sm"
                className="h-6 text-[9px] px-2 gap-1"
                onClick={(e) => { e.stopPropagation(); onAction(chip, defaultOrders[chip.measure_id]); }}
                data-testid={`due-row-order-${chip.measure_id}`}>
                <Zap className="w-2.5 h-2.5" /> Order
              </Button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
